/**
 * Catálogo de agentes nocivos: leitura (#417) e manutenção (#458).
 *
 * A busca varre os 400 agentes por rótulo, número CAS e código do eSocial. A ficha vem
 * numa resposta só: o agente, o rótulo do anexo da NR-15 e todas as opções de limite de
 * tolerância com a norma de origem de cada uma. É norma pública, igual para todo tenant.
 */
import apiClient from './apiClient';

export interface HazardousAgentSummary {
  id: number;
  sourceKey: string;
  displayName: string;
  name: string;
  activity: string | null;
  casNumber: string | null;
  esocialCode: string | null;
  inssAgentCode: string | null;
  family: string | null;
  specialRetirementYears: number | null;
  nr15Annex: number | null;
  unhealthyDegree: string | null;
  degreeNote: string | null;
  hygieneStandards: string | null;
  /**
   * A metodologia que a NORMA prescreve para o agente (#464), e não o estágio da empresa: a
   * técnica por extenso, o fundamento normativo, as vias de penetração e o código GFIP.
   * Opcionais porque o catálogo só as tem nos agentes previdenciários.
   */
  assessmentTypeNaFonte?: string | null;
  assessmentBasis?: string | null;
  penetrationRoutes?: string | null;
  gfipCode?: number | null;
  toleranceLimitNote: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  /** Quantas opções de limite o agente tem. A listagem da manutenção só o preenche quando a API o devolve. */
  toleranceLimitCount?: number;
}

/**
 * Uma opção de limite. São 436 opções para 229 agentes: o mesmo agente aparece com
 * limites de normas diferentes, e qual vale depende do laboratório que mediu.
 */
export interface HazardousAgentToleranceLimit {
  id: number;
  toleranceLimit: string;
  measurementUnit: string | null;
  source: string | null;
  /** Verdadeiro só no limite que o LTCAT e o PGR imprimem como o do catálogo. */
  usadoNosDocumentos?: boolean;
}

export interface HazardousAgentSheet extends HazardousAgentSummary {
  nr15AnnexLabel: string | null;
  toleranceLimits: HazardousAgentToleranceLimit[];
}

export type HazardousAgentStatus = 'ACTIVE' | 'INACTIVE';
export type UnhealthyDegree = 'MINIMO' | 'MEDIO' | 'MAXIMO';

export interface HazardousAgentListParams {
  q?: string;
  status?: HazardousAgentStatus;
  page?: number;
  limit?: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface HazardousAgentListResponse {
  data: HazardousAgentSummary[];
  pagination: Pagination;
}

/** `undefined` = não mexe no campo; `null` = limpa o campo. */
export interface HazardousAgentUpdateInput {
  unhealthyDegree?: UnhealthyDegree | null;
  nr15Annex?: number | null;
  hygieneStandards?: string | string[] | null;
}

export interface ToleranceLimitInput {
  toleranceLimit: string;
  measurementUnit?: string | null;
  source: string;
}

export type HazardousAgentChangeAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'INACTIVATE' | 'REACTIVATE';

export interface HazardousAgentChange {
  id: number;
  limitId: number | null;
  userId: number;
  userName: string;
  action: HazardousAgentChangeAction;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export interface HazardousAgentHistoryResponse {
  data: HazardousAgentChange[];
  pagination: Pagination;
}

class HazardousAgentService {
  private baseUrl = '/hazardous-agents';

  async search(query: string, limit = 20): Promise<HazardousAgentSummary[]> {
    const response = await apiClient.get(
      `${this.baseUrl}/search?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    return response.data?.data ?? response.data;
  }

  async getSheet(id: number | string): Promise<HazardousAgentSheet> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data?.data ?? response.data;
  }

  async list(params: HazardousAgentListParams = {}): Promise<HazardousAgentListResponse> {
    const response = await apiClient.get(this.baseUrl, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  }

  async update(id: number, input: HazardousAgentUpdateInput): Promise<HazardousAgentSheet> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, input);
    return response.data.data;
  }

  async setStatus(id: number, status: HazardousAgentStatus): Promise<HazardousAgentSheet> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, { status });
    return response.data.data;
  }

  async createLimit(id: number, input: ToleranceLimitInput): Promise<HazardousAgentToleranceLimit> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/limits`, input);
    return response.data.data;
  }

  async updateLimit(
    id: number,
    limitId: number,
    input: ToleranceLimitInput
  ): Promise<HazardousAgentToleranceLimit> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/limits/${limitId}`, input);
    return response.data.data;
  }

  async deleteLimit(id: number, limitId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}/limits/${limitId}`);
  }

  async history(id: number, page = 1, limit = 20): Promise<HazardousAgentHistoryResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/history`, { params: { page, limit } });
    return { data: response.data.data, pagination: response.data.pagination };
  }
}

export const hazardousAgentService = new HazardousAgentService();
export default hazardousAgentService;
